import { useEffect } from 'react';
import { findBlock } from '../utils/blockUtils';
import { stripHtml } from '../utils/caretUtils';
import {
  consumeListStartBackspace,
  handleIndentKeyDown,
  shouldHandleLineStartBackspace,
} from '../utils/indentUtils';

const LINE_START_TYPES = new Set(['text', 'heading', 'bullet', 'number', 'checkbox']);

/**
 * Tab / Backspace at line start, read from the LIVE block tree.
 * Per-block contenteditable handlers go stale when only indent changes
 * (react-contenteditable skips the update), so this capture listener is
 * the source of truth.
 */
export const useLineStartKeys = ({
  editorRootRef,
  blocksRef,
  onUpdate,
  onMergeWithPrevious,
}) => {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Backspace' && e.key !== 'Tab') return;
      const root = editorRootRef.current;
      if (!root) return;

      const active = document.activeElement;
      const editable = (e.target.closest?.('.doc-editable') ||
        (active?.classList?.contains('doc-editable') ? active : null));
      if (!editable || !root.contains(editable)) return;
      if (editable.closest('.code-block')) return;

      const blockId = editable.closest('[data-block-id]')?.getAttribute('data-block-id');
      const block = findBlock(blocksRef.current, blockId);
      if (!block || !LINE_START_TYPES.has(block.type)) return;

      if (e.key === 'Tab') {
        handleIndentKeyDown(e, block, onUpdate, editable);
        return;
      }

      const liveContent = editable.innerHTML || block.content || '';
      const isEmpty = stripHtml(liveContent).trim() === '';
      if (
        !shouldHandleLineStartBackspace(e, editable, {
          isEmpty,
          blockId: block.id,
          content: liveContent,
        })
      ) {
        return;
      }
      consumeListStartBackspace(e, block, {
        content: liveContent,
        onUpdate,
        onMergeWithPrevious,
      });
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [editorRootRef, blocksRef, onUpdate, onMergeWithPrevious]);
};
